import React, { useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Toolbar, Typography, Paper, IconButton, Tooltip, styled, Box, Checkbox, Card, CardActionArea, CardContent, CardActions, Menu, MenuList, ListItemIcon, MenuItem, ListItemText, Hidden } from '@mui/material';
import { Folder, Image } from '../models';
import * as FolderStore from '../store/Folder';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import { useNavigate } from 'react-router-dom';
import { stopClickPropogation } from '../utils';
import CopyButton from './CopyButton';
import _ from 'lodash';

import FolderIcon from '@mui/icons-material/FolderOutlined';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import FolderUpIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import MoveIcon from '@mui/icons-material/DriveFileMoveOutlined';
import DownloadImageButton from './DownloadImageButton';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ContentCopy } from '@mui/icons-material';

interface FilesGridProps {
    folders: Folder[];
    images: Image[];
    currentFolder: Folder;
    selectedImageIds: string[];
    onRefresh: (folder: Folder) => void;
    onCreateFolder: (parentFolder: Folder) => void;
    onDeleteFolder: (folder: Folder) => void;
    onDeleteImages: (images: Image[]) => void;
    onMoveImages: (images: Image[]) => void;
    onMoveFolder: (folder: Folder) => void;
    onToggleAllImages: () => void;
    onCheckImage: (image: Image) => void;
}

const Container = styled('div')(({ theme }) => ({
    padding: theme.spacing(2)
}));

const FolderCard = styled(Card)(({ theme }) => ({
    width: 180,
    height: 180,
    display: 'inline-block',
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const ImageCard = styled(Card)(({ theme }) => ({
    width: 180,
    height: 180,
    display: 'inline-block',
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: 'relative'
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
}));

const StyledCardActionArea = styled(CardActionArea)(({ theme }) => ({

}));

const StyledImageIcon = styled(ImageIcon)({
    fontSize: 90,
    color: '#757575'
});

const StyledFolderUpIcon = styled(FolderUpIcon)({
    fontSize: 90,
    color: '#757575'
});

const StyledFolderOutlinedIcon = styled(FolderOutlinedIcon)({
    fontSize: 90,
    color: '#757575'
});

const StyledCardActions = styled(CardActions)({
    display: 'flex',
    height: 42
});

const CardName = styled(Typography)({
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const StyledCheckbox = styled(Checkbox)({
    position: 'absolute',
    top: 4,
    left: 4,
    zIndex: 100,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        backgroundOpacity: 0.1
    }
});

function CardTools(props: { onMove: () => void, onDelete: () => void, refUrl: string }) {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [copyComplete, setCopyComplete] = React.useState<boolean>(false);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMove = () => {
        props.onMove();
        handleClose();
    }

    const handleDelete = () => {
        props.onDelete();
        handleClose();
    }

    const handleCopy = () => {
        navigator.clipboard.writeText(props.refUrl);
        setCopyComplete(true);
        setTimeout(() => { handleClose(); setCopyComplete(false); }, 1000);
    }

    return (
        <>
            <Tooltip title="Options">
                <IconButton aria-label="options" onClick={handleClick}>
                    <MoreVertIcon />
                </IconButton>
            </Tooltip>
            <Menu
                open={open}
                onClose={handleClose}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <MenuList>
                    <MenuItem onClick={handleCopy}>
                        <ListItemIcon>
                            <ContentCopy fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{copyComplete ? 'Copied!' : 'Copy Ref Url'}</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleMove}>
                        <ListItemIcon>
                            <MoveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Move</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleDelete}>
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                </MenuList>
            </Menu>
        </>
    )
}

export default function FilesGrid(props: FilesGridProps) {
    const foldersFlattened = useSelector(FolderStore.selectors.foldersFlattened);
    const parentFolder = foldersFlattened.find(f => f.id === props.currentFolder.parentId);
    const anyImageSelected = props.selectedImageIds.length > 0;
    const imagesSorted = _.orderBy(props.images, [image => image.fileName.toLowerCase()], ['asc']);
    const foldersSorted = _.orderBy(props.folders, [folder => folder.name.toLowerCase()], ['asc']);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const createDeleteImageCallback = (image: Image) => () => {
        props.onDeleteImages([image]);
    }

    const createMoveImageCallback = (image: Image) => () => {
        props.onMoveImages([image]);
    }

    const createDeleteFolderCallback = (folder: Folder) => (event: React.MouseEvent<unknown>) => {
        event.stopPropagation();
        props.onDeleteFolder(folder);
    }

    const createMoveFolderCallback = (folder: Folder) => (event: React.MouseEvent<unknown>) => {
        event.stopPropagation();
        props.onMoveFolder(folder);
    }

    const createFolderClickCallback = (folder: Folder) => () => {
        navigate(`/folder/${folder.id}`);
    }

    const createImageClickCallback = (image: Image) => () => {
        navigate(`/image/${image.id}`);
    }

    const isImageSelected = (image: Image) => {
        return props.selectedImageIds.includes(image.id);
    }

    const allImagesSelected = () => {
        return props.images.length > 0 && props.images.length === props.selectedImageIds.length;
    }

    const createCheckImageCallback = (image: Image) => (event: React.ChangeEvent<HTMLInputElement>) => {
        props.onCheckImage(image);
    }

    const renderImage = (image: Image) => {
        const isSelected = isImageSelected(image);
        const handleImageClick = createImageClickCallback(image);

        const handleClick = () => {
            if (anyImageSelected) {
                props.onCheckImage(image);
                return;
            }
            handleImageClick();
        }

        return (
            <ImageCard key={image.id} style={{ backgroundColor: isSelected ? '#F5F5F5' : 'inherit' }}>
                <StyledCheckbox color="primary" checked={isSelected} onChange={createCheckImageCallback(image)} onClick={stopClickPropogation} />
                <StyledCardActionArea onClick={handleClick}>
                    <StyledCardContent
                        style={{
                            height: 90,
                            width: 148,
                            backgroundImage: `url('${image.resizeUrl}?w=148')`,
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                        }}
                    >
                    </StyledCardContent>
                </StyledCardActionArea>
                <StyledCardActions>
                    <Tooltip title={image.fileName}>
                        <CardName variant="caption">{image.fileName}</CardName>
                    </Tooltip>
                    <CardTools onDelete={createDeleteImageCallback(image)} onMove={createMoveImageCallback(image)} refUrl={image.referenceRawUrl || image.rawUrl} />
                </StyledCardActions>
            </ImageCard>
        );
    }

    const renderFolder = (folder: Folder) => {
        return (
            <FolderCard key={folder.id}>
                <StyledCardActionArea onClick={createFolderClickCallback(folder)}>
                    <StyledCardContent>
                        <StyledFolderOutlinedIcon />
                    </StyledCardContent>
                </StyledCardActionArea>
                <StyledCardActions>
                    <Tooltip title={folder.name}>
                        <CardName variant="caption">{folder.name}</CardName>
                    </Tooltip>
                </StyledCardActions>
            </FolderCard>
        );
    }

    const renderParentFolder = () => {
        if (!parentFolder) {
            return null;
        }
        return (
            <FolderCard square>
                <StyledCardActionArea onClick={createFolderClickCallback(parentFolder)}>
                    <StyledCardContent>
                        <StyledFolderUpIcon />
                    </StyledCardContent>
                </StyledCardActionArea>
                <StyledCardActions>
                    <CardName variant="caption">Parent</CardName>
                </StyledCardActions>
            </FolderCard>
        );
    }

    return (
        <Container>
            {renderParentFolder()}
            {foldersSorted.map(f => renderFolder(f))}
            {imagesSorted.map(i => renderImage(i))}
        </Container>
    );
}